import { useEffect, useRef } from 'react';
import { reportAcquisition } from '@/api/acquisition';
import { getStoredAttribution, clearStoredAttribution } from '@/lib/attribution';

// Per-user flag: keyed by userId so a second user signing up on the same
// device/tab (shared laptop, demo machine) still gets reported. The flag only
// avoids redundant calls — the backend is the source of truth either way.
const reportedFlagKey = (userId: string) => `rosie:attribution-reported:${userId}`;

/**
 * Report acquisition attribution once after first successful auth.
 * Always fires (even with no captured params) so every new user gets an
 * acquired_at timestamp server-side; the backend is first-non-empty-touch-wins,
 * so repeat or empty reports never clobber real attribution.
 */
export function useAttributionReport(isAuthenticated: boolean, userId: string | undefined): void {
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !userId || attemptedRef.current) return;
    try {
      if (sessionStorage.getItem(reportedFlagKey(userId))) return;
    } catch {
      /* fall through — best effort */
    }
    attemptedRef.current = true;
    const stored = getStoredAttribution();
    reportAcquisition(stored ?? { params: {} })
      .then(() => {
        try {
          sessionStorage.setItem(reportedFlagKey(userId), '1');
        } catch {
          /* ignore */
        }
        clearStoredAttribution();
      })
      .catch((error: unknown) => {
        // A 4xx is non-retryable: the same blob would fail every session until
        // its TTL — drop it. 5xx/network errors keep the blob; the recovery
        // path is the NEXT page load (isAuthenticated never flips again within
        // a normal session, so the attemptedRef reset alone rarely re-fires).
        // `| undefined` in the cast keeps the optional chain honest (and
        // eslint-clean): a promise can reject with undefined.
        const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
        if (status !== undefined && status >= 400 && status < 500) {
          clearStoredAttribution();
        }
        attemptedRef.current = false;
      });
  }, [isAuthenticated, userId]);
}
